import { DsdEditorDocumentContent } from "./document-content";
import { buildDsdEditorSidebarCategories } from "./document-sidebars";
import type { DsdEditorDocumentContentModel } from "./document-view-types";
import { useDocumentScroll } from "./use-document-scroll";
import { useDsdEditorCellSelection } from "./use-dsd-editor-cell-selection";
import { cn } from "@/lib/utils";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import { useLayoutEffect, useMemo, useRef } from "react";

/** 문서 본문 로직. 레이아웃(사이드바·헤더·패널)은 뷰가 가진다 */
export const useDsdEditorDocumentBody = (
  model: DsdEditorDocumentContentModel,
) => {
  const { selection, scrollRequest } = model;
  const { sections, items } = model.document;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // 새로고침 시 브라우저가 복원하는 중첩 스크롤 위치 무시하고 항상 상단부터 시작.
  // layout effect 여야 한다 — 같은 커밋에 마운트되는 ReviewCellHint 는 이 초기화 뒤(effect)에 대상 셀로 스크롤한다.
  // 이걸 effect 로 바꾸면 초기화가 그 스크롤을 지운다 (review-cell-hint.test.tsx)
  useLayoutEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, []);
  const itemsBySection = useMemo(() => {
    const map = new Map<string, DsdEditorParsedItem[]>();

    for (const item of items) {
      const sectionItems = map.get(item.sectionName) ?? [];
      sectionItems.push(item);
      map.set(item.sectionName, sectionItems);
    }

    return map;
  }, [items]);

  const renderedSections = useMemo(() => {
    const orderedSections = [...sections];

    for (const item of items) {
      if (!orderedSections.includes(item.sectionName)) {
        orderedSections.push(item.sectionName);
      }
    }

    return orderedSections.filter((sectionName) =>
      itemsBySection.has(sectionName),
    );
  }, [sections, items, itemsBySection]);
  const sidebarCategories = useMemo(
    () => buildDsdEditorSidebarCategories(renderedSections),
    [renderedSections],
  );
  const { activeSection, scrollToSection } = useDocumentScroll({
    items,
    renderedSections,
    scrollContainerRef,
    scrollRequest,
  });

  // 표 셀 드래그 다중 선택 + Ctrl+C 복사. 드래그 종료 시 click 이 공통 조상(스크롤
  // 컨테이너)에서 발화하므로, 다중 선택 중에는 아래 onScrollAreaClick 의 "빈 영역 클릭 → 리뷰
  // 선택 해제"가 오발하지 않도록 ref 로 가드한다.
  const multiSelectActiveRef = useRef(false);
  useDsdEditorCellSelection(scrollContainerRef, {
    onMultiSelectChange: (isMulti) => {
      multiSelectActiveRef.current = isMulti;
      // 다중 선택이 단일 클릭 focus 를 이긴다 — 시작 시점에 리뷰 선택을 "빈 곳 클릭"과
      // 같은 시맨틱으로 걷어, 클릭 ring 과 드래그 테두리가 겹쳐 남지 않게 한다.
      // (대사 뷰·ixd-dsd DSD 프리뷰의 상호배타 규칙과 동일)
      if (isMulti) selection?.onClearSelection?.();
    },
  });
  const onScrollAreaClick = () => {
    // 드래그 다중 선택 종료 click(공통 조상 발화)을 빈 영역 클릭으로 오인해 리뷰 선택을 해제하지 않는다.
    if (multiSelectActiveRef.current) return;
    selection?.onClearSelection?.();
  };

  // 셀 선택 뒤 부드럽게 스크롤되는 동안 다음 셀을 누르면 내용이 손 밑에서 움직여 클릭이 빗나가거나
  // 드래그 다중 선택으로 오인된다. 누르는 순간 진행 중인 스크롤을 그 자리에 멈춘다
  // (같은 위치로 즉시 스크롤하면 진행 중인 부드러운 스크롤이 취소된다)
  const freezeScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollTop, behavior: "instant" });
  };

  return {
    scrollContainerRef,
    renderedSections,
    itemsBySection,
    sidebarCategories,
    activeSection,
    scrollToSection,
    onScrollAreaClick,
    freezeScroll,
  };
};

export type DsdEditorDocumentBody = ReturnType<typeof useDsdEditorDocumentBody>;

export const DsdEditorDocumentScrollArea = ({
  body,
  model,
  contentClassName,
  frameClassName,
}: {
  body: DsdEditorDocumentBody;
  model: DsdEditorDocumentContentModel;
  contentClassName?: string;
  frameClassName?: string;
}) => {
  const {
    selection,
    noteReferenceReview,
    correctionReview,
    sumValidationReview,
    companyNameReview,
  } = model;

  return (
    <div className="relative min-h-0 min-w-0 flex-1">
      <div
        ref={body.scrollContainerRef}
        // overscroll-none: 스크롤 체이닝도 막고 macOS 고무줄 튕김도 없앤다 — 튕기는 동안 셀에 붙은 근거 창(fixed)과 내용이 어긋난다
        className="h-full overflow-y-auto overscroll-none bg-background-neutral-alternative"
        onClick={body.onScrollAreaClick}
        onPointerDownCapture={body.freezeScroll}
      >
        <div className={cn("flex flex-col px-5 pb-5 pt-5", contentClassName)}>
          <DsdEditorDocumentContent
            sections={body.renderedSections}
            itemsBySection={body.itemsBySection}
            selectedCellId={selection?.selectedCellId ?? null}
            noteReferenceReview={noteReferenceReview}
            correctionReview={correctionReview}
            sumValidationReview={sumValidationReview}
            companyNameReview={companyNameReview}
            onCellSelect={selection?.onCellSelect}
            canSelectCell={selection?.canSelectCell}
            frameClassName={frameClassName}
          />

          {model.document.items.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <p className="body-1-normal-medium text-label-assistive">
                표시할 항목이 없습니다.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
